import type { App } from 'vue'
import { zIndexContextKey } from 'element-plus'
import type { UserModule } from '~/types'

const HEADER_Z_INDEX_VAR = '--dd-header-z-index'

/**
 * Element Plus hands every popper, dialog and drawer a z-index from a counter that
 * starts at 2000, which is under the app header (see `styles/layers.scss`). A
 * dropdown that Popper flips upward near the top of a short window then ended up
 * behind the header. Starting the counter at the header's own level fixes that for
 * every Element Plus surface at once, instead of each one opting in.
 *
 * Provided on the app rather than through `<el-config-provider>` so it also reaches
 * imperative overlays (`ElMessageBox`), which render outside any component tree we
 * mount.
 */
export function setupElementPlusZIndex(app: App) {
  // Read on first use, not at install: Element Plus unrefs it when an overlay
  // opens, long after the stylesheet has applied.
  app.provide(
    zIndexContextKey,
    computed(() => {
      if (typeof document === 'undefined') {
        return undefined
      }
      const raw = getComputedStyle(document.documentElement)
        .getPropertyValue(HEADER_Z_INDEX_VAR)
        .trim()
      const zIndex = Number(raw)
      if (!raw || Number.isNaN(zIndex)) {
        throw new Error(
          `${HEADER_Z_INDEX_VAR} is not defined — is styles/layers.scss loaded?`
        )
      }
      return zIndex
    })
  )
}

export const install: UserModule = ({ app }) => {
  setupElementPlusZIndex(app)
}
